import { Plus, X } from 'lucide-react';
import { useState } from 'react';
import type {
  ApiEndpoint,
  DbTable,
  FeatureTechSpecDelta,
  PageRoute,
  UXFlow,
} from '~/lib/tech-spec/types';

type SectionType = 'pages' | 'endpoints' | 'dbTables' | 'uxFlows';

interface Props {
  type: SectionType;
  pages?: PageRoute[];
  endpoints?: ApiEndpoint[];
  dbTables?: DbTable[];
  uxFlows?: UXFlow[];
  onCustomize: (delta: Partial<FeatureTechSpecDelta>) => void;
}

const METHOD_COLORS: Record<ApiEndpoint['method'], string> = {
  GET: 'bg-blue-100 text-blue-700',
  POST: 'bg-green-100 text-green-700',
  PUT: 'bg-amber-100 text-amber-700',
  PATCH: 'bg-amber-100 text-amber-700',
  DELETE: 'bg-red-100 text-red-700',
};

export default function TechSpecSection({
  type,
  pages = [],
  endpoints = [],
  dbTables = [],
  uxFlows = [],
  onCustomize,
}: Props) {
  const [showAdd, setShowAdd] = useState(false);
  const [addPath, setAddPath] = useState('');
  const [addLabel, setAddLabel] = useState('');
  const [addDesc, setAddDesc] = useState('');
  const [addMethod, setAddMethod] = useState<ApiEndpoint['method']>('GET');

  function removeItem(id: string) {
    if (type === 'pages') {
      onCustomize({ pages: { added: [], removedIds: [id] } });
    } else if (type === 'endpoints') {
      onCustomize({ endpoints: { added: [], removedIds: [id] } });
    } else if (type === 'dbTables') {
      onCustomize({ dbTables: { added: [], removedIds: [id] } });
    } else {
      onCustomize({ uxFlows: { added: [], removedIds: [id] } });
    }
  }

  function addItem() {
    if (!addPath && !addLabel) return;
    const id = `custom-${Date.now()}`;
    if (type === 'pages') {
      onCustomize({
        pages: {
          removedIds: [],
          added: [
            {
              id,
              path: addPath,
              label: addLabel,
              description: addDesc,
              authRequired: false,
            },
          ],
        },
      });
    } else if (type === 'endpoints') {
      onCustomize({
        endpoints: {
          removedIds: [],
          added: [
            {
              id,
              method: addMethod,
              path: addPath,
              description: addDesc,
            },
          ],
        },
      });
    }
    setAddPath('');
    setAddLabel('');
    setAddDesc('');
    setShowAdd(false);
  }

  const isEmpty =
    (type === 'pages' && pages.length === 0) ||
    (type === 'endpoints' && endpoints.length === 0) ||
    (type === 'dbTables' && dbTables.length === 0) ||
    (type === 'uxFlows' && uxFlows.length === 0);

  return (
    <div>
      {isEmpty && !showAdd && (
        <p className="text-xs text-gray-400 py-2">
          None for this stack configuration.
        </p>
      )}

      {type === 'pages' && pages.length > 0 && (
        <div className="divide-y divide-gray-100">
          {pages.map((page) => (
            <div
              key={page.id}
              className="flex items-start justify-between py-2 gap-2 group"
            >
              <div className="min-w-0">
                <code className="text-xs font-mono text-indigo-700 bg-indigo-50 px-1.5 py-0.5 rounded">
                  {page.path}
                </code>
                <span className="text-xs text-gray-500 ml-2">{page.label}</span>
                <p className="text-xs text-gray-400 mt-0.5">
                  {page.description}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span
                  className={`text-xs px-1.5 py-0.5 rounded ${page.authRequired ? 'bg-gray-100 text-gray-500' : 'bg-green-50 text-green-700'}`}
                >
                  {page.authRequired ? 'auth' : 'public'}
                </span>
                <button
                  type="button"
                  onClick={() => removeItem(page.id)}
                  className="opacity-0 group-hover:opacity-100 p-0.5 text-gray-400 hover:text-red-500 transition-opacity"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {type === 'endpoints' && endpoints.length > 0 && (
        <div className="divide-y divide-gray-100">
          {endpoints.map((ep) => (
            <div
              key={ep.id}
              className="flex items-start justify-between py-2 gap-2 group"
            >
              <div className="min-w-0">
                <span
                  className={`text-xs font-mono font-semibold px-1.5 py-0.5 rounded mr-2 ${METHOD_COLORS[ep.method]}`}
                >
                  {ep.method}
                </span>
                <code className="text-xs font-mono text-gray-700">
                  {ep.path}
                </code>
                <p className="text-xs text-gray-400 mt-0.5">{ep.description}</p>
                {ep.requestBody && (
                  <p className="text-xs text-gray-400 mt-0.5">
                    Body: <span className="font-mono">{ep.requestBody}</span>
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => removeItem(ep.id)}
                className="opacity-0 group-hover:opacity-100 flex-shrink-0 p-0.5 text-gray-400 hover:text-red-500 transition-opacity"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {type === 'dbTables' && dbTables.length > 0 && (
        <div className="space-y-3">
          {dbTables.map((table) => (
            <div key={table.id} className="group">
              <div className="flex items-center justify-between mb-1">
                <code className="text-xs font-mono font-semibold text-gray-800 bg-gray-100 px-2 py-0.5 rounded">
                  {table.name}
                </code>
                <button
                  type="button"
                  onClick={() => removeItem(table.id)}
                  className="opacity-0 group-hover:opacity-100 p-0.5 text-gray-400 hover:text-red-500 transition-opacity"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              {table.notes && (
                <p className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded mb-1">
                  {table.notes}
                </p>
              )}
              {table.columns.length > 0 && (
                <div className="rounded-lg border border-gray-100 overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-2 py-1.5 text-gray-500 font-medium">
                          Column
                        </th>
                        <th className="text-left px-2 py-1.5 text-gray-500 font-medium">
                          Type
                        </th>
                        <th className="text-left px-2 py-1.5 text-gray-500 font-medium">
                          Null?
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {table.columns.map((col) => (
                        <tr key={col.name}>
                          <td className="px-2 py-1.5 font-mono text-gray-700">
                            {col.name}
                          </td>
                          <td className="px-2 py-1.5 text-gray-500">
                            {col.type}
                            {col.notes && (
                              <span className="text-gray-400 ml-1">
                                — {col.notes}
                              </span>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-gray-400">
                            {col.nullable ? 'yes' : 'no'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {type === 'uxFlows' && uxFlows.length > 0 && (
        <div className="space-y-4">
          {uxFlows.map((flow) => (
            <div key={flow.id} className="group">
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-sm font-medium text-gray-800">
                  {flow.title}
                </p>
                <button
                  type="button"
                  onClick={() => removeItem(flow.id)}
                  className="opacity-0 group-hover:opacity-100 p-0.5 text-gray-400 hover:text-red-500 transition-opacity"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              <ol className="space-y-1">
                {flow.steps.map((step, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-xs text-gray-600"
                  >
                    <span className="flex-shrink-0 h-4 w-4 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center font-medium text-[10px] mt-0.5">
                      {i + 1}
                    </span>
                    {step}
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      )}

      {/* Add item inline form */}
      {showAdd && (type === 'pages' || type === 'endpoints') && (
        <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-2">
          {type === 'endpoints' && (
            <div className="flex gap-2">
              <select
                value={addMethod}
                onChange={(e) =>
                  setAddMethod(e.target.value as ApiEndpoint['method'])
                }
                className="text-xs border border-gray-300 rounded px-2 py-1 bg-white"
              >
                {(['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const).map(
                  (m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ),
                )}
              </select>
              <input
                value={addPath}
                onChange={(e) => setAddPath(e.target.value)}
                placeholder="/api/path"
                className="flex-1 text-xs border border-gray-300 rounded px-2 py-1 font-mono"
              />
            </div>
          )}
          {type === 'pages' && (
            <div className="flex gap-2">
              <input
                value={addPath}
                onChange={(e) => setAddPath(e.target.value)}
                placeholder="/page-path"
                className="flex-1 text-xs border border-gray-300 rounded px-2 py-1 font-mono"
              />
              <input
                value={addLabel}
                onChange={(e) => setAddLabel(e.target.value)}
                placeholder="Page label"
                className="flex-1 text-xs border border-gray-300 rounded px-2 py-1"
              />
            </div>
          )}
          <input
            value={addDesc}
            onChange={(e) => setAddDesc(e.target.value)}
            placeholder="Description"
            className="w-full text-xs border border-gray-300 rounded px-2 py-1"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={addItem}
              className="text-xs px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700"
            >
              Add
            </button>
            <button
              type="button"
              onClick={() => setShowAdd(false)}
              className="text-xs px-3 py-1 border border-gray-300 rounded text-gray-600 hover:bg-gray-100"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {(type === 'pages' || type === 'endpoints') && !showAdd && (
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          className="mt-2 flex items-center gap-1 text-xs text-gray-400 hover:text-indigo-600 transition-colors"
        >
          <Plus className="h-3.5 w-3.5" />
          Add {type === 'pages' ? 'page' : 'endpoint'}
        </button>
      )}
    </div>
  );
}
